// A seção Conexões: os canais e os portais da revenda, e o assistente que adiciona um canal. (#880)

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Estado } from '@/shared/ui/estados/estados';
import {
  useApi,
  useCanaisConectados,
  useCanaisDeWhatsApp,
  useCatalogoDePlataformas,
  useIntegracoes,
} from '@/shared/api/consultas';
import type { CanalDeWhatsApp, TipoDeCanal } from '@cliqcrm/contracts';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { type UsoDeStepUp } from '@/features/acesso/confirmar-identidade';
import { conectarWhatsAppPelaMeta, SignupCanceladoError } from '../lib/embedded-signup';
import { aplicativoDaMeta } from '@/shared/config/configuracao';
import { AtendentesDoCanal } from './atendentes-do-canal';
import { DesconectarCanal } from './desconectar-canal';
import { CartaoDoNumero } from './cartao-do-numero';
import { ConectarPaginaDaMeta, type EstadoDaMeta } from './conectar-pagina-da-meta';
import { consumirRetornoDaMeta } from '../lib/retorno-da-meta';
import { ConfigurarCanalSimples, InstalacaoDoCanal, type TipoSimples } from './canais-simples';
import { CartaoDoCanal, disponibilidadeDoCanal, GradeDeCanais, PassosDoAssistente } from './canais';
import { Botao, Cartao, Chip, Dialogo, Selo } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import type { TomSelo } from '@/shared/ui';
import { Campo } from './partes-da-secao';

export function SecaoConexoes({
  stepUp,
  canalPedido,
  aoAtenderPedido,
}: {
  stepUp: UsoDeStepUp;
  canalPedido: TipoDeCanal | null;
  aoAtenderPedido: () => void;
}): ReactNode {
  const consulta = useIntegracoes();

  return (
    <section className="admin__secao" aria-label="Conexões">
      <h3 className="admin__subtitulo">Integrações</h3>
      <p className="admin__apoio">
        Os sistemas que sustentam o cadastro e o atendimento. São mantidos pela Cliqfy: aqui você
        acompanha a saúde, e o suporte resolve o resto.
      </p>

      <Estado
        consulta={consulta}
        forma="cartoes"
        linhas={2}
        vazio={{
          quando: (l) => l.itens.length === 0,
          titulo: 'Nenhuma integração conectada',
          porque: 'As integrações desta organização ainda não foram ligadas. Fale com o suporte.',
        }}
      >
        {(lista) => (
          <ul className="admin__conexoes">
            {lista.itens.map((c) => (
              <Cartao como="li" variante="folgado" key={c.provider}>
                <div className="admin__conexao-cabeca">
                  <span className="admin__conexao-nome">{c.rotulo}</span>
                  <SeloDaConexao status={c.status} />
                </div>
                <dl className="admin__campos admin__campos--densos ficha">
                  {/*
                    O ENDEREÇO saiu desta ficha.

                    Ele era o nome interno do serviço no compose —
                    `http://chatwoot-rails:3000` —, que não resolve em navegador
                    nenhum e não diz nada a quem administra uma revenda. Pior:
                    é desenho da nossa infraestrutura numa tela de cliente, e a
                    §14 já manda esconder o motor (aqui ele é "Chat" e "CRM",
                    não Chatwoot e Twenty).

                    Quem precisa do endereço é o suporte, e o suporte tem o
                    `admin__conexoes` do lado de dentro — a rota continua
                    devolvendo `base_url`.
                  */}
                  <Campo rotulo="Conta" valor={c.conta_externa} />
                  <Campo
                    rotulo="Credencial"
                    // NUNCA o valor, nem mascarado: o servidor não o devolve.
                    valor={c.credencial_configurada ? '••• configurada' : 'não configurada'}
                  />
                  {c.provider_version && <Campo rotulo="Versão" valor={c.provider_version} />}
                </dl>
              </Cartao>
            ))}
          </ul>
        )}
      </Estado>

      <CanaisDaRevenda
        stepUp={stepUp}
        canalPedido={canalPedido}
        aoAtenderPedido={aoAtenderPedido}
      />

      <PortaisDaRevenda />
    </section>
  );
}

/**
 * Os portais de anúncio — e SÓ quando esta revenda tem algum.
 *
 * ── Por que a seção some ──────────────────────────────────────────────────
 *
 * Antes ela era um texto fixo prometendo OLX e Webmotors "junto com o primeiro
 * conector". Numa revenda que não anuncia em portal nenhum, isso é uma seção
 * inteira sobre algo que ela não usa e não vai usar — e uma tela de
 * administração cheia de promessa ensina a passar o olho sem ler.
 *
 * `ativa_no_tenant` é a chave de funcionalidade da revenda (princípio 08): ela
 * responde exatamente "esta revenda ligou este portal?".
 *
 * ── Enquanto carrega, nada ────────────────────────────────────────────────
 *
 * Sem esqueleto de carregamento: a seção pode simplesmente não existir para
 * esta revenda, e um esqueleto anunciaria conteúdo que talvez nunca apareça —
 * o que é pior que esperar meio segundo por algo que existe.
 */
function PortaisDaRevenda(): ReactNode {
  const consulta = useCatalogoDePlataformas();
  const ligados = (consulta.data?.itens ?? []).filter((p) => p.ativa_no_tenant);

  if (ligados.length === 0) return null;

  return (
    <>
      <h3 className="admin__subtitulo">Portais</h3>
      <p className="admin__apoio">
        Os portais de anúncio ligados nesta organização. As credenciais são dela e são administradas
        aqui.
      </p>
      <ul className="admin__conexoes">
        {ligados.map((portal) => (
          <li key={portal.provider} className="admin__conexao">
            <div className="admin__conexao-cabeca">
              <span className="admin__conexao-nome">{portal.rotulo}</span>
              {/*
                A situação vem do catálogo e é dita como ela é.

                `fichado` significa que sabemos o que o portal exige e NÃO há
                código que fale com ele. Mostrar um portal fichado sem esse
                aviso o faria parecer conectado — é a confusão que a própria
                distinção do catálogo existe para evitar.
              */}
              <Chip>{SITUACAO_DO_PORTAL[portal.situacao] ?? portal.situacao}</Chip>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

const SITUACAO_DO_PORTAL: Readonly<Record<string, string>> = {
  fichado: 'não disponível',
  implementado: 'em testes',
  homologado: 'conectado',
};

/**
 * `+551920187093` → `+55 19 2018-7093`.
 *
 * O motor guarda em E.164, que é o certo para máquina e ruim para gente: um
 * número corrido de treze dígitos não se confere de relance, e conferir é
 * exatamente o que se faz olhando esta lista.
 *
 * Formata só o que reconhece — Brasil, com DDI, DDD e oito ou nove dígitos. O
 * resto sai como veio: um número estrangeiro mal formatado seria pior que um
 * número corrido.
 */
function formatarTelefone(e164: string): string {
  const casou = /^\+55(\d{2})(\d{4,5})(\d{4})$/.exec(e164);
  if (!casou) return e164;
  return `+55 ${casou[1]} ${casou[2]}-${casou[3]}`;
}

/**
 * Conectar o número de WhatsApp da revenda (G02, §8.2, §12.1).
 *
 * ── Por que o botão é tão pequeno para o que ele faz ──────────────────────
 *
 * Porque a revenda não digita nada. O caminho manual existe — pede
 * `phone_number_id`, `business_account_id` e um token gerado à mão no painel da
 * Meta —, e é o que estava aqui antes de existir tela nenhuma. São três valores
 * em telas diferentes, e o token costuma sair com validade errada.
 *
 * No Embedded Signup a pessoa clica, escolhe a conta no popup da Meta, e pronto.
 *
 * ── A ordem: popup primeiro, step-up depois ───────────────────────────────
 *
 * O popup fica FORA do `comStepUp`, e isso é deliberado. `comStepUp` refaz a
 * ação depois da concessão — se o popup estivesse dentro, ele abriria duas
 * vezes, e a segunda pediria à pessoa que escolhesse a conta de novo sem
 * explicação.
 *
 * Fora, a segunda tentativa reaproveita o mesmo `code`. Isso é seguro porque o
 * servidor confere papel e step-up ANTES de falar com o motor: o 403 não gasta
 * o `code`.
 *
 * O preço é que o `code` vale poucos minutos. Se a reautenticação demorar mais
 * que isso, a Meta o recusa — e a mensagem que volta do servidor já começa por
 * essa causa, que é a mais frequente de todas.
 */
function CanaisDaRevenda({
  stepUp,
  canalPedido,
  aoAtenderPedido,
}: {
  stepUp: UsoDeStepUp;
  /** O canal escolhido na loja de funcionalidades: o assistente abre nele. */
  canalPedido: TipoDeCanal | null;
  aoAtenderPedido: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const meta = aplicativoDaMeta();
  const [erro, definirErro] = useState<string | null>(null);
  const [conectado, definirConectado] = useState<{ nome: string; inbox: number } | null>(null);
  /*
   * O assistente de adicionar canal, no formato do motor (29/09/2026): nulo é
   * a lista; os passos são escolher o canal, configurar e pronto.
   */
  const [assistente, definirAssistente] = useState<Assistente>(() =>
    canalPedido ? { passo: 'configurar', tipo: canalPedido, meta: null } : null,
  );

  // O pedido da loja é atendido uma vez: voltar a Conexões depois, pelo menu,
  // abre a lista, e não o mesmo assistente de novo. E o assistente vem à
  // vista: ele fica abaixo das integrações, e a rolagem da loja não serve aqui.
  const tituloDoAssistente = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!canalPedido) return;
    tituloDoAssistente.current?.scrollIntoView?.({ block: 'start' });
    aoAtenderPedido();
  }, [canalPedido, aoAtenderPedido]);
  /** O Web Chat cuja "Instalação" está aberta, na lista. */
  const [instalacaoDe, definirInstalacaoDe] = useState<{ nome: string; texto: string } | null>(
    null,
  );
  /** O canal cujo "Desconectar" está aberto, na lista (30/09/2026). */
  const [desconectando, definirDesconectando] = useState<{ inbox: string; nome: string } | null>(
    null,
  );
  /** O canal que acabou de sair, para a tela confirmar. */
  const [desconectado, definirDesconectado] = useState<string | null>(null);
  /** O canal cujo "Atendentes" está aberto, na lista. */
  const [atendentesDe, definirAtendentesDe] = useState<{ inbox: string; nome: string } | null>(
    null,
  );
  const canais = useCanaisDeWhatsApp();
  const todos = useCanaisConectados();

  /*
   * A volta da Meta (29/09/2026): a casca já abriu esta seção, e aqui o
   * assistente continua de onde parou — no passo de configurar, buscando as
   * páginas que a autorização alcança. Uma vez só: o `code` é de uso único.
   */
  const retornoTratado = useRef(false);
  useEffect(() => {
    if (retornoTratado.current || api === null) return;
    retornoTratado.current = true;
    const retorno = consumirRetornoDaMeta();
    if (!retorno) return;

    if (retorno.tipo === 'cancelado') {
      definirAssistente({ passo: 'configurar', tipo: retorno.canal, meta: null });
      definirErro('A autorização na Meta foi cancelada. Tente de novo quando quiser.');
      return;
    }

    definirAssistente({ passo: 'configurar', tipo: retorno.canal, meta: 'buscando' });
    void api
      .paginasDaMeta({ corpo: { code: retorno.code, state: retorno.state } })
      .then((paginas) =>
        definirAssistente((atual) =>
          atual?.passo === 'configurar' ? { ...atual, meta: paginas } : atual,
        ),
      )
      .catch((falha: unknown) => {
        definirAssistente({ passo: 'configurar', tipo: retorno.canal, meta: null });
        definirErro(
          recadoDeErro(falha, {
            generica: 'Não foi possível ler as páginas da sua conta. Entre com o Facebook de novo.',
          }),
        );
      });
  }, [api]);

  // A mesma frase para a falha da mutação e para a da conexão refeita depois
  // do step-up, que roda fora dela.
  function mostrarFalhaDaConexao(falha: unknown): void {
    // Desistir não é falha: quem fechou a janela sabe o que fez, e uma
    // mensagem vermelha aqui ensinaria a ignorar as que importam.
    if (falha instanceof SignupCanceladoError) return;
    definirErro(
      recadoDeErro(falha, {
        generica: 'Não foi possível conectar o número.',
        comCodigoDeAtendimento: true,
      }),
    );
  }

  function atualizarListasDeCanais(): void {
    void cache.invalidateQueries({ queryKey: chaves.administracao() });
    // Sem isto, o número recém-conectado só apareceria depois de recarregar
    // a página — que é metade do defeito que esta lista veio consertar.
    void cache.invalidateQueries({ queryKey: chaves.canaisDeWhatsApp() });
    void cache.invalidateQueries({ queryKey: chaves.canaisConectados() });
  }

  const conectar = useMutation({
    mutationFn: async () => {
      definirErro(null);
      definirConectado(null);

      if (!meta) {
        // Erro interno, não texto de tela: quem chega aqui é um clique que a
        // interface já devia ter impedido, e o recado da tela é o de cima.
        throw new Error('Conectar WhatsApp não está disponível nesta conta.');
      }

      const daMeta = await conectarWhatsAppPelaMeta(meta);

      await stepUp.comStepUp(
        async () => {
          const criado = await api!.conectarWhatsAppPorSignup({
            corpo: {
              code: daMeta.code,
              business_id: daMeta.businessId,
              waba_id: daMeta.wabaId,
              // A chave some quando o popup não entregou o número: mandar vazio
              // faria o motor procurar um número chamado "" em vez de descobrir
              // o da conta.
              ...(daMeta.phoneNumberId ? { phone_number_id: daMeta.phoneNumberId } : {}),
            },
          });
          definirConectado({ nome: criado.nome, inbox: criado.inbox_id });
          // Aqui, e não no `onSuccess`: com step-up, esta conexão roda depois de
          // a mutação já ter terminado, e a lista ficava sem o número novo.
          atualizarListasDeCanais();
          // Dentro do assistente, o fim é o passo "Pronto"; na lista ("Trocar
          // conta"), o recado logo abaixo dela.
          definirAssistente((atual) =>
            atual
              ? { passo: 'atendentes', nome: criado.nome, inbox: String(criado.inbox_id) }
              : atual,
          );
        },
        'whatsapp',
        mostrarFalhaDaConexao,
      );
    },
    onError: mostrarFalhaDaConexao,
  });

  const configurado = meta !== null;
  const semConexao = 'Sem conexão agora. Tente de novo em instantes.';

  /*
   * A lista junta as duas leituras: a de TODOS os canais dá a ordem e os que
   * não são WhatsApp; a de WhatsApp dá o cartão completo do número, com
   * modelos e mensagens automáticas. Um número que só a segunda conhece
   * (motor respondendo pela metade) entra no fim, em vez de sumir.
   */
  const doWhatsApp = new Map((canais.data?.itens ?? []).map((c) => [c.referencia_externa, c]));
  const conectados = todos.data?.itens ?? [];
  const jaListados = new Set(conectados.map((c) => c.referencia_externa));
  const soNoWhatsApp = (canais.data?.itens ?? []).filter(
    (c) => !jaListados.has(c.referencia_externa),
  );
  const nenhum = conectados.length === 0 && soNoWhatsApp.length === 0;

  const cartaoDoNumero = (canal: CanalDeWhatsApp): ReactNode => (
    <CartaoDoNumero
      key={canal.referencia_externa}
      canal={canal}
      telefone={formatarTelefone(canal.telefone)}
      acoes={
        <>
          {configurado && (
            <Botao
              desabilitado={conectar.isPending || api === null}
              motivo={api === null ? semConexao : 'Conectando…'}
              onClick={() => conectar.mutate()}
            >
              Trocar conta
            </Botao>
          )}
          <Botao
            onClick={() =>
              definirAtendentesDe({ inbox: canal.referencia_externa, nome: canal.nome })
            }
          >
            Atendentes
          </Botao>
          <Botao
            variante="perigo"
            onClick={() =>
              definirDesconectando({ inbox: canal.referencia_externa, nome: canal.nome })
            }
          >
            Desconectar
          </Botao>
        </>
      }
    />
  );

  /** `null` quando o canal pode ser escolhido na grade; senão, o porquê. */
  const disponivel = (tipo: TipoDeCanal): string | null =>
    disponibilidadeDoCanal(tipo, configurado);

  if (assistente) {
    return (
      <>
        <h3 className="admin__subtitulo" ref={tituloDoAssistente}>
          Adicionar canal
        </h3>
        <PassosDoAssistente passos={PASSOS_DO_ASSISTENTE} atual={assistente.passo} />

        {assistente.passo === 'canal' && (
          <div className="passos-canal__corpo">
            <p className="admin__apoio">
              Escolha por onde os clientes vão falar com a organização. As conversas de todos os
              canais chegam no Atendimento.
            </p>
            <GradeDeCanais
              disponivel={disponivel}
              aoEscolher={(tipo) => {
                definirErro(null);
                definirAssistente({ passo: 'configurar', tipo, meta: null });
              }}
            />
            <Botao onClick={() => definirAssistente(null)}>Voltar para os canais</Botao>
          </div>
        )}

        {assistente.passo === 'configurar' && assistente.tipo === 'whatsapp' && (
          <div className="passos-canal__corpo">
            <p className="admin__apoio">
              Conectar o WhatsApp abre uma janela da Meta, onde você escolhe a conta. Nada é
              digitado aqui, e nenhum token passa por esta tela. Ao fim, as conversas daquele número
              passam a chegar no Atendimento.
            </p>
            <div className="passos-canal__acoes">
              <Botao
                variante="primario"
                desabilitado={conectar.isPending || api === null}
                motivo={api === null ? semConexao : 'Conectando…'}
                onClick={() => conectar.mutate()}
              >
                {conectar.isPending ? 'Conectando…' : 'Conectar WhatsApp'}
              </Botao>
              <Botao
                desabilitado={conectar.isPending}
                motivo="Conectando…"
                onClick={() => definirAssistente({ passo: 'canal' })}
              >
                Escolher outro canal
              </Botao>
            </div>
          </div>
        )}

        {assistente.passo === 'configurar' &&
          (assistente.tipo === 'facebook' || assistente.tipo === 'instagram') && (
            <ConectarPaginaDaMeta
              canal={assistente.tipo}
              estado={assistente.meta}
              stepUp={stepUp}
              aoConectar={({ inbox, nome }) => {
                void cache.invalidateQueries({ queryKey: chaves.canaisConectados() });
                definirAssistente({ passo: 'atendentes', nome, inbox });
              }}
              aoEscolherOutro={() => {
                definirErro(null);
                definirAssistente({ passo: 'canal' });
              }}
            />
          )}

        {assistente.passo === 'configurar' &&
          (assistente.tipo === 'web_chat' || assistente.tipo === 'api') && (
            <ConfigurarCanalSimples
              tipo={assistente.tipo}
              aoCriar={(canal) => {
                void cache.invalidateQueries({ queryKey: chaves.canaisConectados() });
                definirAssistente({
                  passo: 'atendentes',
                  nome: canal.nome,
                  inbox: canal.referencia_externa,
                  instalacao:
                    canal.instalacao && (canal.tipo === 'web_chat' || canal.tipo === 'api')
                      ? { tipo: canal.tipo, texto: canal.instalacao }
                      : undefined,
                });
              }}
              aoEscolherOutro={() => definirAssistente({ passo: 'canal' })}
            />
          )}

        {assistente.passo === 'atendentes' && (
          <div className="passos-canal__corpo">
            <p className="admin__apoio">
              Escolha quem atende <strong>{assistente.nome}</strong>. São essas pessoas que recebem
              as conversas que chegam por ele e que aparecem para receber uma conversa passada.
            </p>
            <AtendentesDoCanal
              inboxId={assistente.inbox}
              nomeDoCanal={assistente.nome}
              rotuloDoSalvar="Salvar e continuar"
              aoSalvar={() =>
                definirAssistente({
                  passo: 'pronto',
                  nome: assistente.nome,
                  instalacao: assistente.instalacao,
                })
              }
            />
          </div>
        )}

        {assistente.passo === 'pronto' && (
          <div className="passos-canal__corpo">
            {/* `role="status"`: a resposta chega depois de uma janela que tomou
                o foco, e o leitor de tela precisa ouvir o resultado. */}
            <p className="admin__nota admin__nota--ok" role="status">
              Canal conectado como <strong>{assistente.nome}</strong>. As mensagens dele passam a
              chegar no Atendimento a partir de agora.
            </p>
            {assistente.instalacao && (
              <InstalacaoDoCanal
                tipo={assistente.instalacao.tipo}
                instalacao={assistente.instalacao.texto}
              />
            )}
            <Botao variante="primario" onClick={() => definirAssistente(null)}>
              Voltar para os canais
            </Botao>
          </div>
        )}

        {erro && <Faixa tom="bloqueado">{erro}</Faixa>}
      </>
    );
  }

  return (
    <>
      <h3 className="admin__subtitulo">Canais</h3>
      <p className="admin__apoio">
        Por onde os clientes falam com a organização. As conversas de todos os canais chegam no
        Atendimento.
      </p>

      {/*
        Os canais JÁ conectados vêm primeiro, e antes de qualquer botão.

        A tela mostrava só "Conectar WhatsApp", mesmo com número ligado. Quem
        acabava de conectar não via confirmação nenhuma, e em 02/09/2026 isso
        custou horas: a conexão tinha FUNCIONADO, e como a tela dizia o mesmo de
        antes, ninguém acreditou.
      */}
      {nenhum ? (
        <p className="admin__apoio">Nenhum canal conectado ainda.</p>
      ) : (
        <ul className="lista-canais">
          {conectados.map((canal) => {
            const numero =
              canal.tipo === 'whatsapp' ? doWhatsApp.get(canal.referencia_externa) : undefined;
            return numero ? (
              cartaoDoNumero(numero)
            ) : (
              <CartaoDoCanal
                key={canal.referencia_externa}
                canal={canal}
                acoes={
                  <>
                    {canal.tipo === 'web_chat' && canal.instalacao && (
                      <Botao
                        onClick={() =>
                          definirInstalacaoDe({ nome: canal.nome, texto: canal.instalacao! })
                        }
                      >
                        Instalação
                      </Botao>
                    )}
                    <Botao
                      onClick={() =>
                        definirAtendentesDe({ inbox: canal.referencia_externa, nome: canal.nome })
                      }
                    >
                      Atendentes
                    </Botao>
                    <Botao
                      variante="perigo"
                      onClick={() =>
                        definirDesconectando({ inbox: canal.referencia_externa, nome: canal.nome })
                      }
                    >
                      Desconectar
                    </Botao>
                  </>
                }
              />
            );
          })}
          {soNoWhatsApp.map(cartaoDoNumero)}
        </ul>
      )}

      {!configurado && (
        <p className="admin__nota admin__nota--info">
          Conectar WhatsApp não está disponível nesta conta. Fale com o suporte.
        </p>
      )}

      <p>
        <Botao
          variante="primario"
          onClick={() => {
            definirErro(null);
            definirConectado(null);
            definirAssistente({ passo: 'canal' });
          }}
        >
          Adicionar canal
        </Botao>
      </p>

      {desconectando && (
        <DesconectarCanal
          inboxId={desconectando.inbox}
          nome={desconectando.nome}
          stepUp={stepUp}
          aoFechar={() => definirDesconectando(null)}
          aoDesconectar={(nome) => {
            definirDesconectando(null);
            definirDesconectado(nome);
          }}
        />
      )}

      {desconectado && (
        <p className="admin__nota admin__nota--ok" role="status">
          <strong>{desconectado}</strong> foi desconectado. As mensagens dele não chegam mais ao
          Atendimento.
        </p>
      )}

      {instalacaoDe && (
        <Dialogo
          titulo={`Instalação: ${instalacaoDe.nome}`}
          aoFechar={() => definirInstalacaoDe(null)}
          botoes={<Botao onClick={() => definirInstalacaoDe(null)}>Fechar</Botao>}
        >
          <InstalacaoDoCanal tipo="web_chat" instalacao={instalacaoDe.texto} />
        </Dialogo>
      )}

      {atendentesDe && (
        <Dialogo
          titulo={`Atendentes: ${atendentesDe.nome}`}
          aoFechar={() => definirAtendentesDe(null)}
        >
          <AtendentesDoCanal
            inboxId={atendentesDe.inbox}
            nomeDoCanal={atendentesDe.nome}
            aoSalvar={() => definirAtendentesDe(null)}
            outrasAcoes={<Botao onClick={() => definirAtendentesDe(null)}>Cancelar</Botao>}
          />
        </Dialogo>
      )}

      {conectado && (
        <p className="admin__nota admin__nota--ok" role="status">
          Número conectado como <strong>{conectado.nome}</strong>. As mensagens dele passam a chegar
          no Atendimento a partir de agora.
        </p>
      )}

      {erro && <Faixa tom="bloqueado">{erro}</Faixa>}
    </>
  );
}

type Assistente =
  | null
  | { passo: 'canal' }
  | { passo: 'configurar'; tipo: TipoDeCanal; meta: EstadoDaMeta }
  | { passo: 'atendentes'; nome: string; inbox: string; instalacao?: Instalacao }
  | { passo: 'pronto'; nome: string; instalacao?: Instalacao };
/** O jeito de instalar o canal recém-criado, para o passo "Pronto". */
type Instalacao = { tipo: TipoSimples; texto: string };
const PASSOS_DO_ASSISTENTE = [
  { chave: 'canal', nome: 'Escolher o canal' },
  { chave: 'configurar', nome: 'Configurar' },
  { chave: 'atendentes', nome: 'Quem atende' },
  { chave: 'pronto', nome: 'Pronto' },
] as const;

// ── Fila de descarte (D10) ─────────────────────────────────────────────────
/** O estado da conexão em palavra e em tom — o selo compartilhado só sabe o tom. */
function SeloDaConexao({ status }: { status: string }): ReactNode {
  const tom: TomSelo =
    status === 'ativo'
      ? 'ok'
      : status === 'degradado' || status === 'pausado'
        ? 'atencao'
        : status === 'erro'
          ? 'erro'
          : 'neutro';
  const rotulo =
    status === 'ativo'
      ? 'Funcionando'
      : status === 'degradado'
        ? 'Instável'
        : status === 'erro'
          ? 'Com falha'
          : status === 'pausado'
            ? 'Pausada'
            : 'Desativada';

  return (
    <Selo tom={tom} role="status">
      {rotulo}
    </Selo>
  );
}
